/* Constructeur de lignes de facture/devis, partagé (devis, avoirs, rectificatives,
   factures de solde). Une ligne = { désignation, quantité, prix unitaire HT, TVA % }.
   Extrait de Quotes.tsx pour réutilisation. */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../lib/icons';
import { NumField, eur } from './ui';
import type { CatalogItem } from '../data/live';

// `preLockTva` : taux mémorisé par un appelant (verrou TVA client étranger, voir
// CreateInvoice.tsx) le temps du verrouillage, pour restaurer le bon taux à la
// ligne au déverrouillage. Voyage avec la ligne (pas un tableau parallèle par
// index) pour rester juste quand des lignes sont ajoutées/supprimées entre
// temps. Optionnel, ignoré par LineBuilder lui-même.
export type Line = { d: string; q: number; pu: number; tva: number; preLockTva?: number };

/** Ligne vierge par défaut (TVA 20 %). */
export const blankLine = (): Line => ({ d: '', q: 1, pu: 0, tva: 20 });

/** Mappe les lignes UI vers le payload API (HT en euros, filtre les lignes vides).
    `preLockTva` est un champ d'état purement front (verrou TVA), jamais envoyé. */
export function linesToPayload(lines: Line[]): { description: string; quantity: number; price: number; vat: number }[] {
  return lines
    .filter((l) => l.d.trim() && l.q > 0)
    .map((l) => ({ description: l.d.trim(), quantity: l.q, price: l.pu, vat: l.tva }));
}

export function LineBuilder({
  lines,
  setLines,
  catalog,
  vatLocked,
}: {
  lines: Line[];
  setLines: (l: Line[]) => void;
  catalog?: CatalogItem[];
  // Client étranger : TVA forcée à 0 sur toutes les lignes, le sélecteur ne
  // propose plus qu'une option. Optionnel et sans effet quand absent, LineBuilder
  // étant partagé avec devis/avoirs/rectificatives/soldes.
  vatLocked?: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState('');
  const popRef = useRef<HTMLDivElement>(null);

  // Ferme le picker au clic hors de sa zone (bouton + pop-over) ou sur Échap.
  useEffect(() => {
    if (!pickerOpen) return;
    function onDown(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setPickerOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPickerOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  function upd(i: number, k: keyof Line, v: string | number) {
    const c = lines.slice();
    c[i] = { ...c[i], [k]: v };
    setLines(c);
  }
  function del(i: number) {
    setLines(lines.filter((_, j) => j !== i));
  }
  function pick(item: CatalogItem) {
    setLines([...lines, { d: item.label, q: 1, pu: item.unitPriceCents / 100, tva: vatLocked ? 0 : item.vatRate }]);
    setPickerOpen(false);
    setQ('');
  }
  function addBlank() {
    setLines([...lines, vatLocked ? { ...blankLine(), tva: 0 } : blankLine()]);
  }

  const hasCatalog = !!catalog && catalog.length > 0;
  const filtered = hasCatalog
    ? catalog!.filter((it) => it.label.toLowerCase().includes(q.trim().toLowerCase()))
    : [];

  return (
    <div>
      <div className="lines">
        <div className="linehd">
          <span>Désignation</span>
          <span>Qté</span>
          <span>P.U. HT</span>
          <span>TVA</span>
          <span style={{ textAlign: 'right' }}>Total HT</span>
          <span></span>
        </div>
        {lines.map((l, i) => (
          <div key={i} className="lineitem">
            <input className="ctrl" value={l.d} placeholder="Prestation ou produit" aria-label={`Désignation, ligne ${i + 1}`} onChange={(ev) => upd(i, 'd', ev.target.value)} />
            <NumField className="ctrl" value={l.q} aria-label={`Quantité, ligne ${i + 1}`} onChange={(n) => upd(i, 'q', n)} />
            <NumField className="ctrl" value={l.pu} aria-label={`Prix unitaire HT, ligne ${i + 1}`} onChange={(n) => upd(i, 'pu', n)} />
            <select
              className="ctrl"
              value={vatLocked ? 0 : l.tva}
              disabled={vatLocked}
              aria-label={`Taux de TVA, ligne ${i + 1}`}
              onChange={(ev) => upd(i, 'tva', +ev.target.value)}
            >
              {vatLocked ? (
                <option value={0}>0 % (client étranger)</option>
              ) : (
                <>
                  <option value={20}>20 %</option>
                  <option value={10}>10 %</option>
                  <option value={5.5}>5,5 %</option>
                  <option value={0}>0 %</option>
                </>
              )}
            </select>
            <span className="amt-cell">{eur(l.q * l.pu)}</span>
            <button className="del" aria-label="Supprimer la ligne" onClick={() => del(i)}>
              <Icon name="trash" size={16} />
            </button>
          </div>
        ))}
      </div>
      <div ref={popRef} style={{ display: 'flex', gap: 8, marginTop: 12, position: 'relative', flexWrap: 'wrap' }}>
        <button className="btn btn--soft btn--sm" onClick={addBlank}>
          <Icon name="plus" size={15} />
          Ajouter une ligne
        </button>
        {hasCatalog && (
          <button className="btn btn--soft btn--sm" onClick={() => setPickerOpen((o) => !o)}>
            <Icon name="grid" size={15} />
            Depuis le catalogue
          </button>
        )}
        {pickerOpen && hasCatalog && (
          <div className="catalog-pop" role="listbox" aria-label="Catalogue" style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 20,
            width: 340, maxHeight: 280, overflowY: 'auto',
            background: 'var(--surface, #fff)', border: '1px solid var(--border, #ddd)',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 8,
          }}>
            <input
              className="ctrl" autoFocus placeholder="Rechercher un article…" aria-label="Rechercher un article"
              value={q} onChange={(ev) => setQ(ev.target.value)} style={{ width: '100%', marginBottom: 6 }}
            />
            {filtered.length === 0 && <div style={{ padding: 8, opacity: 0.6 }}>Aucun article.</div>}
            {filtered.map((it) => (
              <button
                key={it.id} className="catalog-pop__item" role="option"
                onClick={() => pick(it)}
                style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 12, padding: '8px 10px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <span>{it.label}</span>
                <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>{eur(it.unitPriceCents / 100)} HT</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
